import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'umi';
import moment from 'moment';
import { useLocal, useOption, useSelectorMap, useWeekDay } from '@/pages/dashboard/utils/customHook';
import { getDateFormatString, handleDownload } from '@/pages/dashboard/utils/utils';
import CardBox from '@/pages/dashboard/components/CardBox';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { formatDataByChartType, xAxisHighLight, xAxisLabelFormat } from '@/pages/dashboard/components/Chart/_config/plug';
import { pieFormatterFunc } from '@/pages/dashboard/components/Chart/_config/utils';
import PieChart from '@/pages/dashboard/components/Chart/components/PieChart';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { dynamicString } from '@home/utils/dynamicString';
import { resolveAndSelectName } from '@/pages/dashboard/utils/metricUtils';
import { getCustomValueColumnTooltip } from '@/pages/dashboard/components/EchartsTooltip/utils';

export default function NewUsersDistributionCo(props: any) {
  const { config: propsConfig, data: propsData, metricsInfo: propsMetricsInfo } = props;
  const { data: defaultData, loading, errorMsg: error } = propsData?.keyMetricsData || {};
  const { isLastDay, type, componentMetrics, titleEn: configTitleEn, titleCn: configTitleCn, headerMappings: configHeaderMappings } = propsConfig;
  const contextObj = useContext(FilterContext);
  const { filterVal } = contextObj || {};
  const { timeType = 'Day', diff, rangeTime, ...fiters } = filterVal || {};
  const formValue = { timeType, filterValue: { rangeTime, ...fiters } };
  const metricsInfo = propsMetricsInfo?.metricsInfo;

  // 卡片数据配置（mediaChannel 买量渠道，与 channel 共用同一数据源）
  const typeToDataMap: { [key: string]: string; } = {
    country: 'country_register_date_list',
    channel: 'channel_register_date_list',
    mediaChannel: 'channel_register_date_list',
    platform: 'system_register_date_list',
  };

  const defaultDataKey = typeToDataMap[type];
  const defaultDataList = defaultData?.[defaultDataKey] || [];

  const { getWeekDay } = useWeekDay();
  const { formatMessage } = useIntl(); // 中英切换方法
  const { getLocalText, locale, isEN } = useLocal('engagement_register', '_');
  const { getFilterItemText } = useSelectorMap();
  const [option, setSwitchData] = useState(defaultDataList);
  const [chartType, setChartType] = useState('');

  const timeTypeObj: { [key: string]: string; } = {
    Day: 'YYYY-MM-DD',
    Week: 'YYYY-MM-DD',
    Month: 'YYYY-MM'
  };



  // 表头名称配置：优先使用 config.headerMappings，否则走本地 key
  const defaultHeaderMappings: { [key: string]: string; } = {
    country: getLocalText('Country'),
    channel: getLocalText('Channel'),
    mediaChannel: getLocalText('Channel'),
    platform: getLocalText('Platform'),
  };
  const headerMappings: { [key: string]: string; } = {};
  (['country', 'channel', 'mediaChannel', 'platform'] as const).forEach(t => {
    const configHeader = configHeaderMappings?.[t];
    if (configHeader) {
      headerMappings[t] = isEN ? configHeader.en : configHeader.cn;
    } else {
      headerMappings[t] = defaultHeaderMappings[t];
    }
  });
  const tableOption = [
    {
      key: 'date',
      align: 'left',
      fixed: 'left',
      width: 100,
      header: getLocalText('Date'),
      render: (cvm: any) => {
        const weekDay = getWeekDay(cvm?.date);
        const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
        return <>
          <span className='table-text'>{cvm?.date}</span>
          {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
        </>;
      },
    },
    {
      key: type,
      align: 'right',
      fixed: 'left',
      width: 100,
      header: headerMappings[type],
      render: (cvm: any) => (<span>{cvm[type]}</span>),
    },
    {
      key: 'Register Users',
      align: 'right',
      fixed: 'left',
      width: 100,
      header: componentMetrics?.enName || componentMetrics?.cnName
        ? resolveAndSelectName(isEN, componentMetrics?.enName, componentMetrics?.cnName, metricsInfo, undefined, getLocalText('Register_Users'))
        : getLocalText('Register_Users'),
      render: (cvm: any) => (<span>{cvm['Register Users']}</span>),
    },
  ];

  const handleAxisFormat = (value: moment.MomentInput) => timeType === "Month" ? moment(value).format('YYYY-MM') : moment(value).format('MM-DD');

  // 指标格式化：优先使用 componentMetrics.formatRule（dynamicString + dataFormat），兼容原逻辑
  const formatValue = (value: string | number) => {
    if (value === null || value === undefined || value === '') return '-';
    if (componentMetrics?.formatRule) {
      return dataFormat({ value, formatRule: dynamicString(componentMetrics.formatRule, propsData) });
    }
    return dataFormat({ value, formatRule: 'thousandsInit' });
  };

  useEffect(() => {
    if (defaultDataList?.length === 0) setSwitchData([]);
    if (!defaultDataList?.length) return;
    // 数据处理
    const options = {
      chart: [],
      table: {}
    };
    // 名称组（mediaChannel 使用 channel 的 selector 类型）
    const selectorType = type === 'mediaChannel' ? 'channel' : type;
    const chartItem = defaultDataList[0].list.map((v: { crz_name: string; full_crz_name: string; }) => ({
      name: getFilterItemText(selectorType, v?.crz_name, v?.full_crz_name),
      stack: "bar",
      type: "bar"
    }));
    // 时间组
    const chartTime = defaultDataList.map((v: { date_ymd: any; }) => v.date_ymd);

    chartItem.forEach((item: { data: any; }, index: string | number) => {
      const yData = chartTime.map((val: string | any[], idx: string | number) => ({
        x: timeType === 'Month' ? val.slice(0, 7) : val,
        y: Number(defaultDataList[idx].list[index].value)
      }));

      item.data = yData;
      return item;
    });
    const table: any = {
      data: [],
      option: []
    };
    const tableData: any[] = [];
    defaultDataList.forEach((item: { list: any[]; date_ymd: any; }) => {
      item.list.forEach(v => {
        tableData.push({
          date: getDateFormatString(timeType, item.date_ymd),
          [type]: getFilterItemText(selectorType, v?.crz_name, v?.full_crz_name),
          'Register Users': formatValue(v.value)
        });
      });
    });
    table.data = tableData;
    // 更新表格列头（支持动态指标名称）
    table.option = tableOption.map((col: any) => {
      if (col.key === 'Register Users') {
        return {
          ...col,
          header: componentMetrics?.enName || componentMetrics?.cnName
            ? resolveAndSelectName(isEN, componentMetrics?.enName, componentMetrics?.cnName, metricsInfo, undefined, getLocalText('Register_Users'))
            : getLocalText('Register_Users')
        };
      }
      return col;
    });
    options.chart = chartItem;
    options.table = table;
    setSwitchData(options);
  // }, [defaultDataList, type, timeType, getFilterItemText, getDateFormatString, formatValue, componentMetrics, metricsInfo, isEN, getLocalText, locale, tableOption]);
  }, [props, isEN]);

  const { xAxisData, legendData, lineSeries, barSeries, isNodata } = useOption(option);
  const { chartData, legendData: pieLegend } = formatDataByChartType('pie', option?.chart, {
    isLastDay,
    isSort: true
  });

  // 走新浮窗处理：指标值用 formatValue（dynamicString + dataFormat）
  const getToolTip = (params: any[]) => getCustomValueColumnTooltip({
    params, options: {
      hasSum: false,
      hasShare: false,
      valueNameCn: '',
      valueNameEn: '',
      isEn: isEN,
      valueFormatRule: componentMetrics?.formatRule ? dynamicString(componentMetrics.formatRule, propsData) : '',
    }
  }, (value) => formatValue(value));

  const renderList = [
    {
      type: 'barChart',
      svgType: 'column-chart',
      content: formatMessage({ id: 'common_bar_chart' }),
      component: <BarChart
        height={340}
        legendData={xAxisData.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData.length > 0 ? barSeries : []}
        formatterFunc={getToolTip}
        setOtherConfigFunc={(opt: any) => {
          opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });
          opt = xAxisHighLight(opt, {
            formatter: (params: { value: string; }) => handleAxisFormat(params.value)
          });
          return opt;
        }}
      />
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      component: <LineChart
        height={340}
        legendData={xAxisData.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData.length > 0 ? lineSeries : []}
        formatterFunc={getToolTip}
        setOtherConfigFunc={(opt: any) => {
          opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });
          opt = xAxisHighLight(opt, {
            formatter: (params: { value: string; }) => handleAxisFormat(params.value)
          });
          return opt;
        }}
      />
    },
    {
      type: 'pieChart',
      svgType: 'pie-chart-icon',
      content: formatMessage({ id: 'common_pie_chart' }),
      component: <PieChart
        height={340}
        isNodata={chartData?.length === 0}
        chartData={chartData}
        legendData={pieLegend}
        formatterFunc={(params: any) => pieFormatterFunc(params, formValue, { isLastDate: isLastDay })} // 饼图浮窗配置
      />
    },
    {
      type: 'sheet',
      svgType: 'sheet',
      content: formatMessage({ id: 'common_table' }),
      component: <MiniTable
        height={340}
        loading={false}
        tableOption={option?.table?.option}
        tableList={option?.table?.data}
      />
    },
  ];
  // 卡片标题：优先 titleEn/titleCn，其次 componentMetrics，最后本地 key（与指标列名分离）
  const titleMappings: Record<string, string> = {
    country: 'Register_Users_Country_Distribution',
    platform: 'Register_Users_Platform_Distribution',
    channel: 'Register_Users_Channel_Distribution',
    mediaChannel: 'Register_Users_Media_Channel_Distribution',
  };
  const defaultTitle = getLocalText(titleMappings[type]);
  let title = defaultTitle;
  if (configTitleEn || configTitleCn) {
    title = resolveAndSelectName(isEN, configTitleEn, configTitleCn, metricsInfo, undefined, defaultTitle);
  } else if (componentMetrics?.enName || componentMetrics?.cnName) {
    title = resolveAndSelectName(isEN, componentMetrics?.enName, componentMetrics?.cnName, metricsInfo, undefined, defaultTitle);
  }

  // 卡片副标题配置
  let subTitleContent;
  if (isLastDay && chartType === 'pieChart') {
    subTitleContent = `${moment(rangeTime?.[1]).format(timeTypeObj[timeType])}`;
  }
  else {
    subTitleContent = `${moment(rangeTime?.[0]).format(timeTypeObj[timeType])}~${moment(rangeTime?.[1]).format(timeTypeObj[timeType])}`;
  }

  return (
    <CardBox
      className='register-table'
      height={340}
      bordered={false}
      titleCom={title}
      subTitleCom={
        <div className='register-table-time'>
          {subTitleContent}
        </div>}
      setCurrentDataType={setChartType}
      renderList={renderList}
      defaultAvtive={2}
      downloadClick={async () => {
        handleDownload(option?.table, title);
      }}
      isLoading={loading}
      isError={error}
      isNodata={isNodata}
    />
  );
}
